/**
 * Pro-only FontAwesome export names → free-pack equivalents.
 * Applied only when the requested export is missing from free packs.
 * Registered under the original iconName so call sites keep working.
 */
export const ICON_ALIASES = {
  // Light / regular pro-only (console warnings)
  faFileChartPie: 'faChartPie',
  faChessQueenAlt: 'faChessQueen',
  faArrowAltToTop: 'faArrowUpFromBracket',
  faUpToLine: 'faArrowUpFromBracket',
  faChartLineUp: 'faChartLine',
  faCommentAltLines: 'faComments',
  faFileChartLine: 'faChartLine',
  faFileChartColumn: 'faChartColumn',
  faFileTimes: 'faFileCircleXmark',
  faFileXmark: 'faFileCircleXmark',
  faFolderCog: 'faFolder',
  faFolderUpload: 'faFileArrowUp',
  faFolderXmark: 'faFolderMinus',
  faMapMarkerExclamation: 'faTriangleExclamation',
  faLocationExclamation: 'faTriangleExclamation',
  faMapMarkerPlus: 'faLocationDot',
  faMoneyCheckEditAlt: 'faMoneyCheckDollar',
  faQuestionSquare: 'faCircleQuestion',
  faRocketLaunch: 'faRocket',
  faStars: 'faStar',
  faTriangle: 'faPlay',
  // Solid typos / pro-only
  faEnvelop: 'faEnvelope',
  faMapMarkerCheck: 'faCircleCheck',
}

/**
 * Free-pack exports whose FA6 iconName differs from the legacy kebab name
 * still used at call sites (e.g. faUsersCog → users-gear, but UI asks for users-cog).
 */
export const ICON_LEGACY_NAMES = {
  faPlusCircle: 'plus-circle',
  faUsersCog: 'users-cog',
}

/**
 * Convert a FontAwesome JS export name (faFolderXmark) to the kebab iconName
 * used by <FontAwesomeIcon icon={['far', 'folder-xmark']} />.
 */
export const faExportToIconName = (faName) =>
  faName
    .replace(/^fa/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase()

const PREFIX_BY_STYLE = {
  brands: 'fab',
  duotone: 'fad',
  light: 'fal',
  regular: 'far',
  solid: 'fas',
}

/**
 * Resolve an icon from free packs, applying aliases and preserving the
 * requested iconName so existing call sites keep working.
 */
export const resolveIconDefinition = ({
  fullIconName,
  style,
  primaryPackage,
  solidPackage,
  regularPackage,
}) => {
  const find = (name) =>
    primaryPackage?.[name] ||
    (style !== 'brands' && solidPackage?.[name]) ||
    (style !== 'brands' && regularPackage?.[name]) ||
    null

  let icon = find(fullIconName)
  let usedAlias = false

  if (!icon) {
    const alias = ICON_ALIASES[fullIconName]
    if (alias) {
      icon = find(alias)
      usedAlias = Boolean(icon)
    }
  }

  if (!icon) return null

  const prefix = PREFIX_BY_STYLE[style] || icon.prefix
  const needsPrefixOverride = prefix !== icon.prefix
  const requestedIconName = faExportToIconName(fullIconName)
  const legacyIconName = ICON_LEGACY_NAMES[fullIconName]

  // Alias / legacy name: keep call-site kebab. Style remap: keep native iconName, fix prefix.
  if (usedAlias || legacyIconName) {
    return {
      ...icon,
      prefix,
      iconName: legacyIconName || requestedIconName,
    }
  }
  if (needsPrefixOverride || style === 'light' || style === 'duotone' || style === 'regular') {
    return { ...icon, prefix }
  }
  return icon
}
